import { getNameOfDayFromDate } from "@@/utilities/date-and-time/name-of-day";
import * as dateFns from "date-fns";
import { toZonedTime } from "date-fns-tz";
import type { LanguageCode } from "../translations/language-code";
import type { Translatable } from "../translations/translation";
import { translation } from "../translations/translation";
import { asDate } from "./as-date";
import type { IsoAndUnixTimestamp, IsoTimestamp } from "./iso-and-unix";
import { nowIsoAndUnix } from "./iso-and-unix";
import { getLocale } from "./locales";
import type { Time } from "./time";
import { timeToStringRounded } from "./time";
import type { TimeRange } from "./time-range";
import { isTimeRange } from "./time-range";
import type { UnixTimestamp } from "./unix";
import { fromUnix } from "./unix";

/**
 * E.g. 18:24 | 6:24 PM
 * @param {LanguageCode} languageCode
 */
const formatTime =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (dateTime: IsoAndUnixTimestamp | Date) => {
        return dateFns.format(toZonedTime(asDate(dateTime), timeZone), "p", {
            locale: getLocale(languageCode),
        });
    };
/**
 * _Exempel_ \
 * en: `05/29/1453, 12:00 AM` \
 * sv: `2021-03-11 08:54`
 * @param {LanguageCode} languageCode
 */
const formatDateAndTime =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (dateTime: IsoAndUnixTimestamp) => {
        return dateFns.format(toZonedTime(asDate(dateTime), timeZone), "Pp", {
            locale: getLocale(languageCode),
        });
    };
/**
 * _Exempel_ \
 * en: `11 Mar 08:58` eller `11 Mar 08:58 2020`
 * sv: `11:e mars 08:58` eller `11 mars 08:58 2020`
 *
 * @param {LanguageCode} languageCode
 */
const formatDateAndTimeLong =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: IsoAndUnixTimestamp,
        options?: {
            includeYear?: boolean;
            includeYearIfNotCurrent?: boolean;
        },
    ) => {
        const date = toZonedTime(asDate(dateTime), timeZone);
        const isCurrentYear = date.getFullYear() === new Date().getFullYear();
        const includeYear =
            options?.includeYear ||
            (options?.includeYearIfNotCurrent && !isCurrentYear);

        return dateFns.format(
            toZonedTime(asDate(dateTime), timeZone),
            "d MMM HH:mm" + (includeYear ? " yyyy" : ""),
            {
                locale: getLocale(languageCode),
            },
        );
    };

/**
 * E.g. en: January (2021)
 * @param {LanguageCode} languageCode
 */
const formatYearAndMonth =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: { unix: UnixTimestamp },
        options?: {
            excludeYearIfCurrent?: boolean;
        },
    ) => {
        const date = toZonedTime(fromUnix(dateTime.unix), timeZone);
        const format =
            options?.excludeYearIfCurrent &&
            date.getUTCFullYear() === new Date().getUTCFullYear()
                ? "MMMM"
                : "MMMM (yyyy)";

        return dateFns.format(date, format, {
            locale: getLocale(languageCode),
        });
    };
/**
 * E.g. en: January (2021)
 * @param {LanguageCode} languageCode
 */
const formatShortMonthAndYear =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: { unix: UnixTimestamp },
        options?: {
            excludeYearIfCurrent?: boolean;
        },
    ) => {
        const date = toZonedTime(fromUnix(dateTime.unix), timeZone);
        return dateFns.format(
            date,
            options?.excludeYearIfCurrent &&
                date.getUTCFullYear() !== new Date().getUTCFullYear()
                ? "MMM (yyyy)"
                : "MMM",
            {
                locale: getLocale(languageCode),
            },
        );
    };

/**
 * _Exempel_ \
 * en: `03/11/2021, 9:17 AM`
 * sv: `2021-03-11 09:20`
 */
const formatDateTime =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (dateTime: { unix: UnixTimestamp }) => {
        return dateFns.format(
            toZonedTime(fromUnix(dateTime.unix), timeZone),
            "Pp",
            {
                locale: getLocale(languageCode),
            },
        );
    };
/**
 * _Exempel_ \
 * en: `03/11/2021`
 * sv: `2021-03-11`
 */
const formatDate =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (dateTime: { unix: UnixTimestamp }) => {
        return dateFns.format(
            toZonedTime(fromUnix(dateTime.unix), timeZone),
            "P",
            {
                locale: getLocale(languageCode),
            },
        );
    };
/**
 * ÅÅ-MM-DD
 * @param {LanguageCode} languageCode
 */
const formatDateShort =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (dateTime: { unix: UnixTimestamp }) => {
        return dateFns.format(
            toZonedTime(fromUnix(dateTime.unix), timeZone),
            "yy-MM-dd",
            {
                locale: getLocale(languageCode),
            },
        );
    };
/**
 * ÅÅ-MM-DD
 * @param {LanguageCode} languageCode
 */
const formatDayOfMonth =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: { unix: UnixTimestamp },
        options?: {
            includeWeekday?: boolean;
            zeroPad?: boolean;
        },
    ) => {
        const dateFormat = options?.zeroPad ? "dd" : "d";
        return dateFns.format(
            toZonedTime(fromUnix(dateTime.unix), timeZone),
            options?.includeWeekday ? `E ${dateFormat}` : dateFormat,
            {
                locale: getLocale(languageCode),
            },
        );
    };
/**
 * _Exempel_ \
 * en: `14 jan.` eller `14 jan. 2020`
 * sv: `11:e mars` eller `11 mars 2020`
 * @param {LanguageCode} languageCode
 */
const formatDateMedium =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: { iso: IsoTimestamp; unix?: UnixTimestamp },
        options?: {
            includeYear?: boolean;
            includeYearIfNotCurrent?: boolean;
            includeDayOfWeek?: boolean;
        },
    ) => {
        const date = toZonedTime(asDate(dateTime), timeZone);
        const locale = getLocale(languageCode);
        const isCurrentYear = date.getFullYear() === new Date().getFullYear();
        const includeYear =
            options?.includeYear ||
            (options?.includeYearIfNotCurrent && !isCurrentYear);
        return dateFns.format(
            date,
            `${options?.includeDayOfWeek ? "E " : ""}do MMM${includeYear ? " yyyy" : ""}`,
            {
                locale,
            },
        );
    };
/**
/**
 * _Exempel_ \
 * sv: `tors 23 maj 15:00` eller `tors 23 maj 2020 15:00`
 * en: `Thurs 23 May 15:00` eller `Thurs 23 May 2020 15:00`
 * @param {LanguageCode} languageCode
 */
const _formatDayDateAndTimeMedium_SingleDate =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: IsoAndUnixTimestamp | Date,
        options?: {
            includeYear?: boolean;
            includeYearIfNotCurrent?: boolean;
        },
    ) => {
        const date = toZonedTime(asDate(dateTime), timeZone);
        const isCurrentYear = date.getFullYear() === new Date().getFullYear();
        const includeYear =
            options?.includeYear ||
            (options?.includeYearIfNotCurrent && !isCurrentYear);
        return `${dateFns.format(
            date,
            `E d MMM` + (includeYear ? " yyyy" : ""),
            {
                locale: getLocale(languageCode),
            },
        )} ${formatTime(languageCode, timeZone)(dateTime)}`;
    };
/**
 * _Exempel_ \
 * sv: `tors 23 maj 15:00` eller `tors 23 maj 2020 15:00`
 * en: `Thurs 23 May 15:00` eller `Thurs 23 May 2020 15:00`
 *
 * Tillåter även time range som input, då returneras en sträng med start och slutdatum \
 * exempel `tors 23 maj 15:00 - tors 23 maj 2025 15:00`
 * @param {LanguageCode} languageCode
 */
const formatDayDateAndTimeMedium =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: IsoAndUnixTimestamp | TimeRange | Date,
        options?: {
            includeYear?: boolean;
            includeYearIfNotCurrent?: boolean;
        },
    ) => {
        if (isTimeRange(dateTime))
            return `${_formatDayDateAndTimeMedium_SingleDate(languageCode, timeZone)(dateTime.start, options)} - ${_formatDayDateAndTimeMedium_SingleDate(languageCode, timeZone)(dateTime.end, options)}`;
        return _formatDayDateAndTimeMedium_SingleDate(languageCode, timeZone)(
            dateTime,
            options,
        );
    };
/**
 * _Exempel_ \
 * en: `Måndag 14 jan.` eller `Måndag 14 jan. 2020`
 * sv: `Fredag 11:e mars` eller `Fredag 11 mars 2020`
 * @param {LanguageCode} languageCode
 */
const formatDateLong =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: IsoAndUnixTimestamp,
        options?: {
            includeYear?: boolean;
        },
    ) => {
        return dateFns.format(
            toZonedTime(asDate(dateTime), timeZone),
            "eeee do MMM" + (options?.includeYear ? " yyyy" : ""),
            {
                locale: getLocale(languageCode),
            },
        );
    };

/**
 * _Exempel_ \
 * en: `Today 13:10` eller `11 mar 13:20`
 * sv: `Idag 13:10` eller `11 mars 13:20`
 * @param {LanguageCode} languageCode
 */
const formatRelativeDateMedium =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (
        dateTime: IsoAndUnixTimestamp,
        options?: {
            includeTime?: boolean;
            includeDate?: boolean;
            includeYear?: boolean;
            includeYearIfNotCurrent?: boolean;
        },
    ) => {
        const date = toZonedTime(asDate(dateTime), timeZone);
        const datePart = dateFns.isToday(date)
            ? translation({
                  sv:
                      "Idag" + options?.includeDate
                          ? ` ${formatDateMedium(
                                languageCode,
                                timeZone,
                            )(dateTime)}`
                          : "",
                  en:
                      "Today" + options?.includeDate
                          ? ` ${formatDateMedium(
                                languageCode,
                                timeZone,
                            )(dateTime)}`
                          : "",
              })
            : dateFns.isTomorrow(date)
              ? translation({
                    sv:
                        "Imorgon" + options?.includeDate
                            ? ` ${formatDateMedium(
                                  languageCode,
                                  timeZone,
                              )(dateTime)}`
                            : "",
                    en:
                        "Tomorrow" + options?.includeDate
                            ? ` ${formatDateMedium(
                                  languageCode,
                                  timeZone,
                              )(dateTime)}`
                            : "",
                })
              : dateFns.isSameWeek(new Date(), date, {
                      weekStartsOn: new Date().getDay() as
                          | 0
                          | 1
                          | 2
                          | 3
                          | 4
                          | 5
                          | 6,
                  })
                ? getNameOfDayFromDate(date) + options?.includeDate
                    ? ` ${formatDateMedium(languageCode, timeZone)(dateTime, {
                          includeYear: options?.includeYear,
                          includeYearIfNotCurrent:
                              options?.includeYearIfNotCurrent,
                      })}`
                    : ""
                : formatDateMedium(languageCode, timeZone)(dateTime, {
                      includeYear: options?.includeYear,
                      includeYearIfNotCurrent: options?.includeYearIfNotCurrent,
                  });
        const timePart = options?.includeTime
            ? formatTime(languageCode, timeZone)(dateTime)
            : "";
        return options?.includeTime
            ? typeof datePart === "string"
                ? `${datePart} ${timePart}`
                : translation({
                      sv: `${datePart.sv} ${timePart}`,
                      en: datePart.en
                          ? `${datePart.en} ${timePart}`
                          : undefined,
                  })
            : datePart;
    };
/**
 * E.g. två månader
 * @param {LanguageCode} languageCode
 */
const formatDistanceFromNowApprox =
    (languageCode: LanguageCode, timeZone = "Europe/Stockholm") =>
    (dateTime: IsoAndUnixTimestamp) => {
        return dateFns.formatDistance(
            toZonedTime(asDate(dateTime), timeZone),
            new Date(),
            {
                locale: getLocale(languageCode),
            },
        );
    };

/**
 * Only + diff at the moment, sorry
 * @param {LanguageCode} languageCode
 */
const formatDistance =
    (timeZone = "Europe/Stockholm") =>
    ({
        fromDateTime,
        toDateTime,
    }: {
        fromDateTime: IsoAndUnixTimestamp;
        toDateTime: IsoAndUnixTimestamp;
    }): {
        differenceInMs: number;
        formatted: Translatable;
        isLate: boolean;
    } => {
        const endDate = toZonedTime(asDate(toDateTime), timeZone);
        const startDate = toZonedTime(asDate(fromDateTime), timeZone);

        const differenceInMs = dateFns.differenceInMilliseconds(
            endDate,
            startDate,
        );
        const differenceInHours = dateFns.differenceInHours(endDate, startDate);

        const differenceInYears = dateFns.differenceInYears(endDate, startDate);
        if (differenceInYears !== 0)
            return {
                isLate: differenceInYears < 0,
                differenceInMs,
                formatted: translation({
                    sv: `${Math.abs(differenceInYears)} år`,
                    en: `${Math.abs(differenceInYears)} year`,
                }),
            };
        const differenceInMonths = dateFns.differenceInMonths(
            endDate,
            startDate,
        );
        if (differenceInMonths !== 0)
            return {
                isLate: differenceInMonths < 0,
                differenceInMs,
                formatted: translation({
                    sv: `${Math.abs(differenceInMonths)} månad${
                        Math.abs(differenceInMonths) > 1 ? "er" : ""
                    }`,
                    en: `${Math.abs(differenceInMonths)} month${
                        Math.abs(differenceInMonths) > 1 ? "s" : ""
                    }`,
                }),
            };
        const differenceInDays = dateFns.differenceInDays(endDate, startDate);
        if (differenceInDays !== 0)
            return {
                isLate: differenceInDays < 0,
                differenceInMs,
                formatted: translation({
                    sv: `${Math.abs(differenceInDays)} dag${
                        Math.abs(differenceInDays) > 1 ? "ar" : ""
                    }`,
                    en: `${Math.abs(differenceInDays)} day${
                        Math.abs(differenceInDays) > 1 ? "s" : ""
                    }`,
                }),
            };

        const hasHours = differenceInHours !== 0;
        const differenceInMinutes =
            dateFns.differenceInMinutes(endDate, startDate) -
            differenceInHours * 60;
        if (differenceInMinutes === 0 && hasHours) {
            return {
                isLate: differenceInHours < 0,
                differenceInMs,
                formatted: translation({
                    sv: `${Math.abs(differenceInHours)} h `,
                    en: `${Math.abs(differenceInHours)} h `,
                }),
            };
        }
        if (Math.abs(differenceInMinutes) > 1)
            return {
                isLate: differenceInMinutes < 0,
                differenceInMs,
                formatted: translation({
                    sv: `${
                        hasHours ? `${Math.abs(differenceInHours)} h ` : ""
                    }${Math.abs(differenceInMinutes)} min`,
                    en: `${
                        hasHours ? `${Math.abs(differenceInHours)} h ` : ""
                    }${Math.abs(differenceInMinutes)} min`,
                }),
            };

        const differenceInSeconds = dateFns.differenceInSeconds(
            endDate,
            startDate,
        );
        if (Math.abs(differenceInSeconds) > 60) {
            return {
                isLate: differenceInSeconds < 0,
                differenceInMs,
                formatted:
                    differenceInSeconds < 0
                        ? translation({
                              sv: `1 min`,
                              en: `1 min`,
                          })
                        : translation({
                              sv: `${Math.abs(differenceInSeconds) - 60} s`,
                              en: `${Math.abs(differenceInSeconds) - 60} s`,
                          }),
            };
        }

        // difference zero, or less than a second
        return {
            isLate: false,
            differenceInMs,
            formatted: translation({ sv: "Nu", en: "Now" }),
        };
    };

const formatDistanceFromNow =
    (timeZone = "Europe/Stockholm") =>
    (toDateTime: IsoAndUnixTimestamp) =>
        formatDistance(timeZone)({
            fromDateTime: nowIsoAndUnix(),
            toDateTime,
        });
/**
 * Only + diff at the moment, sorry
 * @param {LanguageCode} languageCode
 */
const formatDistanceToPickup =
    (timeZone = "Europe/Stockholm") =>
    (pickup: Time) => {
        const now = toZonedTime(asDate(nowIsoAndUnix()), timeZone);
        const pickUpStart = toZonedTime(asDate(pickup.start), timeZone);

        const differenceInHours = dateFns.differenceInHours(pickUpStart, now);
        const differenceInMs = dateFns.differenceInMilliseconds(
            pickUpStart,
            now,
        );

        if (Math.abs(differenceInHours) > 0 || dateFns.isPast(pickUpStart)) {
            const timeStr = timeToStringRounded(pickup, 5, true);

            const isSameMonthOrWeek =
                dateFns.isSameMonth(pickUpStart, new Date()) ||
                dateFns.isSameWeek(pickUpStart, new Date());

            const isCurrentYear = dateFns.isSameYear(pickUpStart, new Date());
            let formattedValue = isSameMonthOrWeek
                ? translation({
                      sv:
                          dateFns.format(
                              pickUpStart,
                              `eee do ${isCurrentYear ? "" : "yyyy, "}`,
                              {
                                  locale: getLocale("sv"),
                              },
                          ) + timeStr,
                      en:
                          dateFns.format(
                              pickUpStart,
                              `eee do ${isCurrentYear ? "" : "yyyy, "}`,
                              {
                                  locale: getLocale("en"),
                              },
                          ) + timeStr,
                  })
                : translation({
                      sv:
                          dateFns.format(
                              pickUpStart,
                              `do MMM ${isCurrentYear ? "" : "yyyy, "}`,
                              {
                                  locale: getLocale("sv"),
                              },
                          ) + timeStr,
                      en:
                          dateFns.format(
                              pickUpStart,
                              `do MMM ${isCurrentYear ? "" : "yyyy, "}`,
                              {
                                  locale: getLocale("en"),
                              },
                          ) + timeStr,
                  });

            if (dateFns.isToday(pickUpStart)) {
                formattedValue = translation({
                    sv: "Idag " + timeStr,
                    en: "Today " + timeStr,
                });
            }

            const isLate = isTimeRange(pickup)
                ? dateFns.isPast(asDate(pickup.end))
                : dateFns.isPast(asDate(pickup.start));

            return {
                isLate: isLate,
                differenceInMs,
                formatted: formattedValue,
            };
        } else {
            return formatDistance(timeZone)({
                fromDateTime: nowIsoAndUnix(),
                toDateTime: pickup.start,
            });
        }
    };

const dateTimeFormatter = (
    languageCode: LanguageCode,
    timeZone = "Europe/Stockholm",
) => ({
    formatTime: formatTime(languageCode, timeZone),
    /**
     * _Exempel_ \
     * en: `03/11/2021`
     * sv: `2021-03-11`
     */
    formatDate: formatDate(languageCode, timeZone),
    /**
     * _Exempel_ \
     * en: `03/11/2021, 9:17 AM`
     * sv: `2021-03-11 09:20`
     */
    formatDateTime: formatDateTime(languageCode, timeZone),
    /**
     * Ex. `ÅÅ-MM-DD`
     */
    formatDateShort: formatDateShort(languageCode, timeZone),
    /**
     * _Exempel_ \
     * en: `05/29/1453, 12:00 AM` \
     * sv: `2021-03-11 08:54`
     */
    formatDateAndTime: formatDateAndTime(languageCode, timeZone),
    /**
     * _Exempel_ \
     * en: `14 jan.` eller `14 jan. 2020`
     * sv: `11:e mars` eller `11 mars 2020`
     */
    formatDateMedium: formatDateMedium(languageCode, timeZone),
    /**
     * _Exempel_ \
     * sv: `tors 23 maj 15:00` eller `tors 23 maj 2020 15:00` \
     * en: `Thurs 23 May 15:00` eller `Thurs 23 May 2020 15:00` \
     *
     * Tillåter även time range som input, då returneras en sträng med start och slutdatum \
     * exempel `tors 23 maj 15:00 - tors 23 maj 2025 15:00`
     */
    formatDayDateAndTimeMedium: formatDayDateAndTimeMedium(
        languageCode,
        timeZone,
    ),
    /**
     * _Exempel_ \
     * en: `Måndag 14 jan.` eller `Måndag 14 jan. 2020`
     * sv: `Fredag 11:e mars` eller `Fredag 11 mars 2020`
     */
    formatDateLong: formatDateLong(languageCode, timeZone),
    /**
     * _Exempel_ \
     * en: `14 jan.` eller `14 jan. 2020`
     * sv: `11:e mars` eller `11 mars 2020`
     */
    formatRelativeDateMedium: formatRelativeDateMedium(languageCode, timeZone),
    /**
     * _Exempel_ \
     * en: `11 Mar 08:58` eller `11 Mar 08:58 2020`
     * sv: `11:e mars 08:58` eller `11 mars 08:58 2020`
     */
    formatDateAndTimeLong: formatDateAndTimeLong(languageCode, timeZone),
    formatDistanceFromNowApprox: formatDistanceFromNowApprox(
        languageCode,
        timeZone,
    ),
    formatYearAndMonth: formatYearAndMonth(languageCode, timeZone),
    formatShortMonthAndYear: formatShortMonthAndYear(languageCode, timeZone),
    formatDistance: formatDistance(timeZone),
    formatDistanceFromNow: formatDistanceFromNow(timeZone),
    formatDistanceToPickup: formatDistanceToPickup(timeZone),
    formatDayOfMonth: formatDayOfMonth(languageCode, timeZone),
});

export {
    dateTimeFormatter,
    formatDateAndTime,
    formatDateAndTimeLong,
    formatTime,
};
